import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import WaitlistedBanner from "@/components/WaitlistedBanner";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import CameraCapture from "@/components/CameraCapture";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Camera, Upload, Loader2, ImagePlus, Sparkles } from "lucide-react";
import { toast } from "sonner";

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

export default function Dashboard() {
    const [loading, setLoading] = useState(false);
    const [note, setNote] = useState("");
    const [preview, setPreview] = useState(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [pendingStream, setPendingStream] = useState(null);
    const fileRef = useRef(null);
    const navigate = useNavigate();
    const { user } = useAuth();
    const isWaitlisted = user?.status === "waitlisted";

    const openCamera = async () => {
        if (isWaitlisted) {
            toast.error("Your account is awaiting admin approval");
            return;
        }
        if (loading) return;
        // Try to acquire the stream INSIDE the user gesture (critical for iOS Safari).
        if (navigator.mediaDevices?.getUserMedia) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: { ideal: "environment" } },
                    audio: false,
                });
                setPendingStream(stream);
                setCameraOpen(true);
                return;
            } catch (e) {
                if (e?.name === "NotAllowedError") {
                    toast.error("Camera permission denied. Use Upload, or allow access in browser settings.");
                    return;
                }
                if (e?.name === "NotFoundError") {
                    toast.error("No camera found on this device. Use Upload instead.");
                    return;
                }
                // For other errors, still open the modal — it'll re-attempt + show a friendly message.
            }
        }
        // Fallback: open modal so its own logic / error UI handles it.
        setCameraOpen(true);
    };

    const analyze = async (file) => {
        if (!file) return;
        if (isWaitlisted) {
            toast.error("Your account is awaiting admin approval");
            return;
        }
        if (!file.type.startsWith("image/")) {
            toast.error("Please upload an image file");
            return;
        }
        setLoading(true);
        try {
            const dataUrl = await fileToBase64(file);
            setPreview(dataUrl);
            const res = await api.post("/analyze", {
                image_base64: dataUrl,
                image_mime: file.type,
                note: note || undefined,
            });
            toast.success("Analysis ready");
            navigate(`/history/${res.data.analysis_id}`, { state: { analysis: res.data } });
        } catch (e) {
            const msg =
                e?.response?.data?.detail || e?.message || "Analysis failed. Try again.";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const analyzeDataUrl = async (dataUrl, mime) => {
        if (!dataUrl) return;
        setCameraOpen(false);
        if (isWaitlisted) {
            toast.error("Your account is awaiting admin approval");
            return;
        }
        setLoading(true);
        try {
            setPreview(dataUrl);
            const res = await api.post("/analyze", {
                image_base64: dataUrl,
                image_mime: mime || "image/jpeg",
                note: note || undefined,
            });
            toast.success("Analysis ready");
            navigate(`/history/${res.data.analysis_id}`, {
                state: { analysis: res.data },
            });
        } catch (e) {
            const msg =
                e?.response?.data?.detail || e?.message || "Analysis failed. Try again.";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Shell>
            <div className="nl-fade-up">
                <WaitlistedBanner />
                <OnboardingChecklist />
                <p className="font-eyebrow mb-3">Today · Scan</p>
                <h1
                    className="font-serif-editorial text-4xl sm:text-5xl tracking-tight leading-[1.02]"
                    style={{ color: "#1A3626" }}
                >
                    What are you
                    <br />
                    <em className="italic font-normal">considering buying?</em>
                </h1>
                <p className="mt-4 text-[15px] text-[#4A5A4E] max-w-md">
                    Photograph the packaging, nutrition label, or produce. We score it
                    against your health in seconds.
                </p>

                {/* Capture area */}
                <div className="mt-8 nl-card relative overflow-hidden">
                    {preview ? (
                        <img
                            src={preview}
                            alt="Preview"
                            className="w-full rounded-2xl aspect-[4/3] object-cover"
                            data-testid="scan-preview"
                        />
                    ) : (
                        <div
                            className="aspect-[4/3] rounded-2xl flex items-center justify-center relative"
                            style={{
                                background:
                                    "linear-gradient(135deg, #EAF0E9 0%, #F9F9F6 100%)",
                            }}
                        >
                            {/* lens brackets */}
                            <div className="absolute top-4 left-4 w-6 h-6 border-t-2 border-l-2 border-[#1A3626]/40 rounded-tl-lg" />
                            <div className="absolute top-4 right-4 w-6 h-6 border-t-2 border-r-2 border-[#1A3626]/40 rounded-tr-lg" />
                            <div className="absolute bottom-4 left-4 w-6 h-6 border-b-2 border-l-2 border-[#1A3626]/40 rounded-bl-lg" />
                            <div className="absolute bottom-4 right-4 w-6 h-6 border-b-2 border-r-2 border-[#1A3626]/40 rounded-br-lg" />
                            <div className="flex flex-col items-center gap-2 text-[#4A5A4E]">
                                <ImagePlus className="w-10 h-10" />
                                <span className="text-sm">No item yet</span>
                            </div>
                        </div>
                    )}

                    <div className="mt-4">
                        <label className="font-eyebrow mb-2 block">Optional note</label>
                        <input
                            type="text"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. breakfast option, low-sodium"
                            className="nl-input"
                            data-testid="scan-note-input"
                        />
                    </div>

                    <div className="mt-5 grid grid-cols-2 gap-3">
                        <button
                            data-testid="scan-camera-btn"
                            onClick={openCamera}
                            className="nl-btn-primary justify-center"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Camera className="w-4 h-4" />
                            )}
                            <span>Camera</span>
                        </button>
                        <button
                            data-testid="scan-upload-btn"
                            onClick={() => {
                                if (isWaitlisted) {
                                    toast.error(
                                        "Your account is awaiting admin approval"
                                    );
                                    return;
                                }
                                if (loading) return;
                                fileRef.current?.click();
                            }}
                            className="nl-btn-secondary flex items-center justify-center gap-2"
                        >
                            <Upload className="w-4 h-4" />
                            <span>Upload</span>
                        </button>
                    </div>

                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="scan-upload-input"
                        onChange={(e) => analyze(e.target.files?.[0])}
                    />
                </div>

                {loading && (
                    <div className="mt-5 nl-card flex items-center gap-3" data-testid="analyzing-state">
                        <Sparkles className="w-5 h-5 animate-pulse" style={{ color: "#3B824E" }} />
                        <div>
                            <p className="font-medium text-[#1A3626]">Analyzing your item…</p>
                            <p className="text-xs text-[#8BA092]">
                                Reading the label, checking against your health profile.
                            </p>
                        </div>
                    </div>
                )}

                <div className="mt-8 grid grid-cols-3 gap-3">
                    <TipCard title="Readable labels" body="Frame ingredients + nutrition panel for best results." />
                    <TipCard title="Good light" body="Natural store light works; avoid glare." />
                    <TipCard title="Update health" body="Go to Settings to refine your profile anytime." />
                </div>
            </div>

            <CameraCapture
                open={cameraOpen}
                initialStream={pendingStream}
                onClose={() => {
                    setCameraOpen(false);
                    setPendingStream(null);
                }}
                onCapture={(dataUrl, mime) => {
                    setPendingStream(null);
                    analyzeDataUrl(dataUrl, mime);
                }}
            />
        </Shell>
    );
}

function TipCard({ title, body }) {
    return (
        <div className="nl-card !p-4">
            <p className="font-eyebrow !text-[10px]">{title}</p>
            <p className="mt-2 text-[12.5px] text-[#4A5A4E] leading-snug">{body}</p>
        </div>
    );
}
